import React, { useEffect, useState } from "react";
import { getPlans, apiCheckout, getVerticals } from "../lib/api";
import { Check, Loader2, Crown, Sparkles, Building2, Factory } from "lucide-react";
import { toast } from "sonner";
import ContactSalesDialog from "./ContactSalesDialog";

const fmt = (n) => `$${Number(n).toLocaleString()}`;

const ICONS = { grow: Sparkles, scale: Crown, optimize: Building2 };

export default function PricingTiers({ dark = false, currentTier, highlight = "scale" }) {
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState("");
  const [verticals, setVerticals] = useState([]);
  const [vertical, setVertical] = useState("grocery");
  const [salesOpen, setSalesOpen] = useState(false);
  const [salesTier, setSalesTier] = useState(null);

  useEffect(() => {
    getPlans().then(setPlans).catch(() => setPlans([]));
    getVerticals().then((d) => setVerticals(d.verticals || [])).catch(() => setVerticals([]));
  }, []);

  const choose = async (plan) => {
    if (plan.custom || !plan.self_serve) {
      setSalesTier(plan.tier);
      setSalesOpen(true);
      return;
    }
    setBusy(plan.tier);
    try {
      const { url } = await apiCheckout(window.location.origin, plan.tier, vertical);
      window.location.href = url;
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Could not start checkout.");
      setBusy("");
    }
  };

  const cardBase = dark ? "bg-white text-forest-800" : "bg-card text-foreground border border-border";
  const activeVertical = verticals.find((v) => v.id === vertical);

  return (
    <div className="w-full max-w-6xl mx-auto space-y-6">
      <ContactSalesDialog open={salesOpen} onOpenChange={setSalesOpen} source="pricing" vertical={vertical} tier={salesTier} title="Contact sales — Optimize (Enterprise)" />
      {verticals.length > 0 && (
        <div className={`rounded-2xl p-5 ${dark ? "bg-white/10" : "bg-card border border-border"}`} data-testid="industry-selector">
          <div className="flex items-center gap-2 mb-3">
            <Factory className={`h-4 w-4 ${dark ? "text-white" : "text-ai"}`} />
            <span className={`text-sm font-bold ${dark ? "text-white" : "text-foreground"}`}>Choose your industry</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {verticals.map((v) => {
              const on = v.id === vertical;
              return (
                <button key={v.id} data-testid={`vertical-option-${v.id}`} onClick={() => setVertical(v.id)}
                  className={`rounded-full border px-4 py-1.5 text-xs font-bold transition-colors ${on
                    ? "border-ai bg-ai/15 text-foreground"
                    : (dark ? "border-white/30 text-white/80 hover:border-white" : "border-border text-muted-foreground hover:border-muted-foreground/50")}`}>
                  {v.name}
                </button>
              );
            })}
          </div>
          {activeVertical && (
            <p className={`mt-2 text-xs ${dark ? "text-white/70" : "text-muted-foreground"}`}>{activeVertical.tagline}</p>
          )}
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-5 w-full" data-testid="pricing-tiers">
      {plans.map((p) => {
        const Icon = ICONS[p.tier] || Sparkles;
        const featured = p.tier === highlight;
        const isCurrent = currentTier === p.tier;
        return (
          <div
            key={p.tier}
            data-testid={`plan-card-${p.tier}`}
            className={`relative rounded-2xl p-6 flex flex-col ${cardBase} ${featured ? "ring-2 ring-ai shadow-2xl md:scale-[1.03]" : "shadow-lg"}`}
          >
            {featured && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-ai px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white">
                Most popular
              </div>
            )}
            <div className="flex items-center gap-2">
              <div className="h-9 w-9 rounded-lg bg-ai/15 text-ai flex items-center justify-center"><Icon className="h-5 w-5" /></div>
              <div>
                <div className="font-heading text-xl font-extrabold">{p.name}</div>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{p.size_label}</div>
              </div>
            </div>
            <p className="mt-3 text-sm text-muted-foreground min-h-[40px]">{p.target}</p>

            <div className="mt-4">
              {p.custom ? (
                <div className="font-mono text-3xl font-extrabold">Custom</div>
              ) : (
                <div className="flex items-end gap-2">
                  <span className="font-mono text-4xl font-extrabold">{fmt(p.price)}</span>
                  <span className="text-muted-foreground mb-1.5 text-sm">/ year</span>
                </div>
              )}
              {!p.custom ? (
                <div className="mt-1 flex items-center gap-2 text-xs">
                  <span className="line-through text-muted-foreground">{fmt(p.retail_price)}/yr retail</span>
                  <span className="rounded-full bg-action-keep/15 text-action-keep px-2 py-0.5 font-bold">50% introductory</span>
                </div>
              ) : (
                <div className="mt-1 text-xs text-muted-foreground">From {fmt(p.retail_price)}/yr · volume pricing</div>
              )}
            </div>

            <button
              data-testid={`plan-select-${p.tier}`}
              disabled={busy === p.tier || isCurrent}
              onClick={() => choose(p)}
              className={`mt-5 w-full flex items-center justify-center gap-2 rounded-full py-2.5 text-sm font-bold uppercase tracking-wider transition-all disabled:opacity-60 ${
                featured || p.custom
                  ? "bg-ai text-white hover:bg-[#e05400] hover:-translate-y-0.5"
                  : "border border-foreground text-foreground hover:bg-forest-900 hover:text-white"
              }`}
            >
              {busy === p.tier && <Loader2 className="h-4 w-4 animate-spin" />}
              {isCurrent ? "Current plan" : p.custom ? "Contact sales" : busy === p.tier ? "Redirecting…" : "Start free trial"}
            </button>

            <ul className="mt-6 space-y-2 text-sm">
              {p.highlights.map((h) => (
                <li key={h} className={`flex items-start gap-2 ${h.endsWith("plus:") ? "font-semibold text-foreground" : "text-muted-foreground"}`}>
                  {!h.endsWith("plus:") && <Check className="h-4 w-4 text-action-keep mt-0.5 shrink-0" />}
                  {h}
                </li>
              ))}
            </ul>

            <div className="mt-5 pt-4 border-t border-border/60 text-xs text-muted-foreground">
              <span className="font-semibold text-foreground">Onboarding:</span> {p.implementation}
            </div>
          </div>
        );
      })}
      </div>
    </div>
  );
}
